// frontend/src/components/Events/EventProgramModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Chip,
  Avatar,
  TextField,
  CircularProgress,
  Skeleton,
  Alert,
  alpha,
  useTheme,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle
} from '@mui/material';
import {
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AccessTime as AccessTimeIcon,
  MenuBook as MenuBookIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  DragIndicator as DragIndicatorIcon,
  Star as StarIcon,
  Coffee as CoffeeIcon,
  LunchDining as LunchDiningIcon,
  EmojiPeople as EmojiPeopleIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { scheduleAPI, participantsAPI } from '../../services/eventsAPI';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// 🔥 ИМПОРТЫ ДЛЯ DRAG-AND-DROP
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ============================================
// СТИЛИЗОВАННЫЕ КОМПОНЕНТЫ
// ============================================

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    padding: theme.spacing(2),
  },
  '& .MuiDialog-paper': {
    width: 'min(620px, calc(100vw - 32px))',
    maxWidth: 620,
    minWidth: 0,
    height: 'min(90vh, 900px)',
    maxHeight: 'calc(100vh - 32px)',
    margin: 0,
    overflow: 'hidden',
    borderRadius: theme.spacing(4.5),
    border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
    background: `
      linear-gradient(145deg,
        ${alpha(theme.palette.common.white, 0.74)},
        ${alpha(theme.palette.grey[100], 0.46)} 46%,
        ${alpha(theme.palette.primary.main, 0.08)}
      ),
      radial-gradient(circle at 8% 4%, ${alpha(theme.palette.info.light, 0.15)}, transparent 34%),
      radial-gradient(circle at 92% 90%, ${alpha(theme.palette.primary.main, 0.13)}, transparent 42%)
    `,
    backgroundColor: alpha(theme.palette.common.white, 0.56),
    backdropFilter: 'blur(30px) saturate(150%)',
    WebkitBackdropFilter: 'blur(30px) saturate(150%)',
    boxShadow: `0 28px 90px ${alpha(theme.palette.common.black, 0.22)}`,
    zIndex: 1300,
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.common.black, 0.32),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
  color: 'text.primary',
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.50)}, ${alpha(theme.palette.common.white, 0.14)})`,
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.62)}`,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(3),
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.grey[100],
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.grey[400],
    borderRadius: '4px',
    '&:hover': {
      background: theme.palette.grey[600],
    },
  }
}));

const BlockCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isplenary'
})(({ theme, isplenary }) => ({
  padding: theme.spacing(1.55, 1.8),
  marginBottom: theme.spacing(1),
  borderRadius: theme.spacing(3),
  background: `
    linear-gradient(145deg,
      ${alpha(theme.palette.common.white, 0.56)},
      ${alpha(theme.palette.common.white, 0.24)}
    )
  `,
  backgroundColor: alpha(theme.palette.common.white, 0.52),
  border: `1px solid ${alpha(
    isplenary ? theme.palette.warning.main : theme.palette.common.white,
    isplenary ? 0.30 : 0.70
  )}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.045)}`,
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease, background-color 220ms ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    borderColor: alpha(isplenary ? theme.palette.warning.main : theme.palette.primary.main, 0.36),
    backgroundColor: alpha(isplenary ? theme.palette.warning.main : theme.palette.primary.main, 0.035),
    boxShadow: `0 11px 30px ${alpha(theme.palette.common.black, 0.065)}`,
  },
}));

const BlockHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.1),
  width: '100%',
}));

const SpeakerRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.2),
  padding: theme.spacing(0.8, 1.1),
  marginLeft: theme.spacing(3.7),
  marginTop: theme.spacing(0.75),
  marginBottom: theme.spacing(0.75),
  borderRadius: theme.spacing(2.4),
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.44)}, ${alpha(theme.palette.common.white, 0.18)})`,
  backgroundColor: alpha(theme.palette.common.white, 0.42),
  border: `1px solid ${alpha(theme.palette.common.white, 0.58)}`,
  boxShadow: `0 5px 16px ${alpha(theme.palette.common.black, 0.035)}`,
}));

const TimeChip = styled(Chip)(({ theme, color }) => ({
  borderRadius: 999,
  backgroundColor: alpha(color || theme.palette.primary.main, 0.10),
  color: color || theme.palette.primary.main,
  fontWeight: 700,
  minWidth: 84,
  height: 34,
  border: `1px solid ${alpha(color || theme.palette.primary.main, 0.16)}`,
  '& .MuiChip-icon': {
    color: 'inherit',
    fontSize: 18,
  },
  '&:hover': {
    backgroundColor: alpha(color || theme.palette.primary.main, 0.17),
    cursor: 'pointer',
  },
}));

const PlenaryBadge = styled(Chip)(({ theme }) => ({
  borderRadius: 999,
  backgroundColor: alpha(theme.palette.warning.main, 0.10),
  color: theme.palette.warning.main,
  fontWeight: 650,
  height: 28,
  border: `1px solid ${alpha(theme.palette.warning.main, 0.16)}`,
  '& .MuiChip-icon': {
    color: theme.palette.warning.main,
    fontSize: 16,
  },
}));

const SectionBadge = styled(Chip)(({ theme, color }) => ({
  borderRadius: 999,
  backgroundColor: alpha(color || theme.palette.primary.main, 0.10),
  color: color || theme.palette.primary.main,
  fontWeight: 650,
  height: 28,
  border: `1px solid ${alpha(color || theme.palette.primary.main, 0.16)}`,
  '& .MuiChip-icon': {
    color: 'inherit',
    fontSize: 16,
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  padding: theme.spacing(1.15, 2.4),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 650,
  backgroundColor: alpha(theme.palette.primary.main, 0.09),
  color: theme.palette.primary.main,
  border: `1px dashed ${alpha(theme.palette.primary.main, 0.42)}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.13),
    borderColor: alpha(theme.palette.primary.main, 0.60),
  },
}));

const FormCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.2),
  borderRadius: theme.spacing(3),
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.54)}, ${alpha(theme.palette.common.white, 0.20)})`,
  backgroundColor: alpha(theme.palette.common.white, 0.46),
  border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.055)}`,
  marginBottom: theme.spacing(2),
}));


// ============================================
// SKELETON ЗАГРУЗКИ ПРОГРАММЫ
// ============================================

const ProgramSkeleton = ({ theme }) => (
  <Box
    sx={{
      animation: 'programSkeletonFadeIn 180ms ease-out',
      '@keyframes programSkeletonFadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      '@keyframes programSkeletonShimmer': {
        '0%': { backgroundPosition: '-420px 0' },
        '100%': { backgroundPosition: '420px 0' },
      },
    }}
  >
    <Box sx={{ mt: 1.2, mb: 2.4 }}>
      <Skeleton
        variant="text"
        width="68%"
        height={42}
        sx={{
          borderRadius: 2,
          background: `linear-gradient(90deg,
            ${alpha(theme.palette.common.white, 0.34)} 25%,
            ${alpha(theme.palette.common.white, 0.68)} 50%,
            ${alpha(theme.palette.common.white, 0.34)} 75%
          )`,
          backgroundSize: '840px 100%',
          animation: 'programSkeletonShimmer 1.6s ease-in-out infinite',
        }}
      />
    </Box>

    {[0, 1, 2].map((day) => (
      <Box
        key={day}
        sx={{
          mb: 1.4,
          p: 1.4,
          borderRadius: 3,
          background: `linear-gradient(145deg,
            ${alpha(theme.palette.common.white, 0.30)},
            ${alpha(theme.palette.common.white, 0.12)}
          )`,
          border: `1px solid ${alpha(theme.palette.common.white, 0.24)}`,
        }}
      >
        <Skeleton
          variant="text"
          width={day === 0 ? '42%' : '34%'}
          height={30}
          sx={{
            borderRadius: 2,
            mb: 1.1,
            background: `linear-gradient(90deg,
              ${alpha(theme.palette.common.white, 0.28)} 25%,
              ${alpha(theme.palette.common.white, 0.58)} 50%,
              ${alpha(theme.palette.common.white, 0.28)} 75%
            )`,
            backgroundSize: '840px 100%',
            animation: 'programSkeletonShimmer 1.6s ease-in-out infinite',
            animationDelay: `${day * 100}ms`,
          }}
        />

        {[0, 1].map((item) => (
          <Box
            key={item}
            sx={{
              mb: item === 0 ? 1 : 0,
              p: 1.55,
              borderRadius: 3,
              background: `linear-gradient(145deg,
                ${alpha(theme.palette.common.white, 0.40)},
                ${alpha(theme.palette.common.white, 0.18)}
              )`,
              border: `1px solid ${alpha(theme.palette.common.white, 0.30)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Skeleton
                variant="rounded"
                width={84}
                height={34}
                sx={{
                  flexShrink: 0,
                  borderRadius: 999,
                  background: `linear-gradient(90deg,
                    ${alpha(theme.palette.common.white, 0.30)} 25%,
                    ${alpha(theme.palette.common.white, 0.62)} 50%,
                    ${alpha(theme.palette.common.white, 0.30)} 75%
                  )`,
                  backgroundSize: '840px 100%',
                  animation: 'programSkeletonShimmer 1.6s ease-in-out infinite',
                }}
              />

              <Box sx={{ flex: 1 }}>
                <Skeleton
                  variant="text"
                  width={item === 0 ? '62%' : '48%'}
                  height={25}
                  sx={{
                    borderRadius: 2,
                    background: `linear-gradient(90deg,
                      ${alpha(theme.palette.common.white, 0.30)} 25%,
                      ${alpha(theme.palette.common.white, 0.62)} 50%,
                      ${alpha(theme.palette.common.white, 0.30)} 75%
                    )`,
                    backgroundSize: '840px 100%',
                    animation: 'programSkeletonShimmer 1.6s ease-in-out infinite',
                  }}
                />
                <Skeleton
                  variant="text"
                  width={item === 0 ? '78%' : '64%'}
                  height={20}
                  sx={{
                    borderRadius: 2,
                    background: `linear-gradient(90deg,
                      ${alpha(theme.palette.common.white, 0.24)} 25%,
                      ${alpha(theme.palette.common.white, 0.52)} 50%,
                      ${alpha(theme.palette.common.white, 0.24)} 75%
                    )`,
                    backgroundSize: '840px 100%',
                    animation: 'programSkeletonShimmer 1.6s ease-in-out infinite',
                    animationDelay: '80ms',
                  }}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    ))}
  </Box>
);

// 🔥 КОМПОНЕНТ ДЛЯ ПЕРЕТАСКИВАЕМОГО БЛОКА
const SortableBlock = ({
  block,
  children,
  isDragDisabled
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: block.id,
    data: {
      type: 'block',
      block: block
    },
    disabled: isDragDisabled
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        mb: 2,
        cursor: isDragDisabled ? 'default' : 'grab',
        '&:active': {
          cursor: isDragDisabled ? 'default' : 'grabbing'
        }
      }}
    >
      {children}
    </Box>
  );
};

// 🔥 КОМПОНЕНТ: Зона для сброса даты
const DateDropZone = ({ date, children }) => {
  const theme = useTheme();
  const { setNodeRef, isOver } = useDroppable({
    id: `date-${date}`,
    data: {
      type: 'date-zone',
      date: date
    }
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        mb: 1.4,
        p: 1.4,
        borderRadius: 3,
        background: isOver
          ? alpha(theme.palette.primary.main, 0.07)
          : alpha(theme.palette.common.white, 0.08),
        border: isOver
          ? `1px dashed ${alpha(theme.palette.primary.main, 0.55)}`
          : `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
        transition: 'all 0.2s'
      }}
    >
      {children}
    </Box>
  );
};

// ============================================
// ОСНОВНОЙ КОМПОНЕНТ
// ============================================

const EventProgramModal = ({
  open,
  onClose,
  eventId,
  eventTitle,
  eventStartDate,
  eventEndDate,
  sections = [],
  currentSectionId = null,
  canEditProgram = false,
  onProgramUpdate,
  userRoles
}) => {
  const theme = useTheme();
  const { user } = useAuth();

  // Определяем режим отображения
  const isSectionPage = !!currentSectionId;

  // Состояния
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Данные
  const [plenarySpeakers, setPlenarySpeakers] = useState([]);
  const [sectionSpeakers, setSectionSpeakers] = useState({});
  const [blocksByDate, setBlocksByDate] = useState({});

  // Состояния для отслеживания изменений
  const [hasChanges, setHasChanges] = useState(false);
  const [hasSpeakerChanges, setHasSpeakerChanges] = useState(false);
  const [originalBlocksByDate, setOriginalBlocksByDate] = useState({});

  // Состояния для редактирования времени
  const [timeEditBlock, setTimeEditBlock] = useState(null);
  const [timeEditValue, setTimeEditValue] = useState('');

  // Для drag overlay
  const [activeBlock, setActiveBlock] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [formData, setFormData] = useState({ title: '' });

  // 🔥 ПРАВА ДЛЯ РАЗНЫХ ТИПОВ ПОЛЬЗОВАТЕЛЕЙ
  const isOrganizer = userRoles?.isOrganizer || false;
  const isCreator = userRoles?.isCreator || false;
  const isPlenaryModerator = userRoles?.isPlenaryModerator || false;

  // Функция для проверки, является ли пользователь модератором секции
  const isSectionModerator = (sectionId) => {
    if (!userRoles?.sectionModerators) return false;
    return userRoles.sectionModerators.includes(sectionId);
  };

  // Организаторы и создатель могут всё
  const canManageEverything = isCreator || isOrganizer;

  // 🔥 ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ

  const getDatesBetween = (start, end) => {
    if (!start || !end) return [];
    const dates = [];
    const currentDate = new Date(start);
    const endDate = new Date(end);
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  const formatDateHeader = (date) => {
    return format(date, 'd MMMM yyyy', { locale: ru });
  };

  // Создаёт дату/время в локальном часовом поясе браузера.
  // Важно: new Date('YYYY-MM-DD') трактует строку как UTC, из-за чего
  // при переносе пункта между днями дата могла сдвигаться.
  const makeLocalDateTime = (dateStr, timeStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hours, minutes] = timeStr.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes, 0, 0);
  };

  const getTimezoneOffsetString = () => {
    const offsetMinutes = -new Date().getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absMinutes = Math.abs(offsetMinutes);
    const hours = Math.floor(absMinutes / 60);
    const minutes = absMinutes % 60;
    return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const recalculateDayTimes = (blocks) => {
    if (!blocks || blocks.length === 0) return blocks;

    return blocks.map((block, index) => {
      const hours = 9 + index;
      const newTime = `${hours.toString().padStart(2, '0')}:00`;
      const updatedBlock = { ...block, time: newTime };
      if (block.startTime) {
        const currentDate = new Date(block.startTime);
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        const date = makeLocalDateTime(dateStr, newTime);
        updatedBlock.startTime = date.toISOString();
      }
      return updatedBlock;
    });
  };

  // Настройка сенсоров
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  // Загрузка данных
  useEffect(() => {
    if (open && eventId) {
      loadProgramData();
    }
  }, [open, eventId]);

  const loadProgramData = async () => {
    setLoading(true);
    setError('');
    try {
      const allParticipants = await participantsAPI.getParticipants({
        event: eventId,
        status: 'approved',
        participation_type: 'speaker'
      });

      // 🔥 СОРТИРУЕМ ДОКЛАДЧИКОВ ПО speaker_order
      const plenary = allParticipants
        .filter(p => p.is_plenary)
        .sort((a, b) => (a.speaker_order || 0) - (b.speaker_order || 0));

      const bySection = {};

      allParticipants.forEach(p => {
        if (!p.is_plenary && p.section) {
          if (!bySection[p.section.id]) {
            bySection[p.section.id] = [];
          }
          bySection[p.section.id].push(p);
        }
      });

      // 🔥 СОРТИРУЕМ ДОКЛАДЧИКОВ В КАЖДОЙ СЕКЦИИ
      Object.keys(bySection).forEach(sectionId => {
        bySection[sectionId].sort((a, b) => (a.speaker_order || 0) - (b.speaker_order || 0));
      });

      setPlenarySpeakers(plenary);
      setSectionSpeakers(bySection);

      const schedule = await scheduleAPI.getSchedule({ event: eventId });

      const plenaryBlocks = [];
      const sectionBlocks = [];
      const custom = [];

      schedule.forEach(item => {
        if (item.is_plenary) {
          plenaryBlocks.push({
            id: `plenary-${item.id}`,
            realId: item.id,
            type: 'plenary',
            title: 'Пленарное заседание',
            startTime: item.start_time,
            time: format(parseISO(item.start_time), 'HH:mm')
          });
        } else if (item.section) {
          const section = sections.find(s => s.id === item.section);
          sectionBlocks.push({
            id: `section-${item.id}`,
            realId: item.id,
            type: 'section',
            sectionId: item.section,
            title: item.title || section?.title || 'Секция',
            color: section?.color,
            startTime: item.start_time,
            time: format(parseISO(item.start_time), 'HH:mm')
          });
        } else if (item.title) {
          custom.push({
            id: `custom-${item.id}`,
            realId: item.id,
            type: 'custom',
            title: item.title,
            startTime: item.start_time,
            time: format(parseISO(item.start_time), 'HH:mm')
          });
        }
      });

      // Получаем все уникальные даты из блоков
      const allBlockDates = new Set();
      [...plenaryBlocks, ...sectionBlocks, ...custom].forEach(block => {
        if (block.startTime) {
          const blockDate = format(parseISO(block.startTime), 'yyyy-MM-dd');
          allBlockDates.add(blockDate);
        }
      });

      // Добавляем даты мероприятия
      const eventDates = getDatesBetween(eventStartDate, eventEndDate);
      eventDates.forEach(date => {
        allBlockDates.add(format(date, 'yyyy-MM-dd'));
      });

      // Сортируем даты
      const sortedDates = Array.from(allBlockDates).sort();

      // Инициализируем объект с пустыми массивами для каждой даты
      const groupedBlocks = {};
      sortedDates.forEach(dateStr => {
        groupedBlocks[dateStr] = [];
      });

      // Добавляем блоки в соответствующие даты
      [...plenaryBlocks, ...sectionBlocks, ...custom].forEach(block => {
        if (block.startTime) {
          const blockDate = format(parseISO(block.startTime), 'yyyy-MM-dd');
          if (groupedBlocks[blockDate]) {
            groupedBlocks[blockDate].push(block);
          } else {
            groupedBlocks[blockDate] = [block];
          }
        }
      });

      // Сортируем внутри каждого дня по времени
      Object.keys(groupedBlocks).forEach(dateStr => {
        groupedBlocks[dateStr].sort((a, b) => {
          const timeA = a.time || '00:00';
          const timeB = b.time || '00:00';
          return timeA.localeCompare(timeB);
        });
      });

      // Сортируем сами дни
      const sortedGroupedBlocks = {};
      Object.keys(groupedBlocks)
        .sort()
        .forEach(key => {
          sortedGroupedBlocks[key] = groupedBlocks[key];
        });

      setBlocksByDate(sortedGroupedBlocks);
      setOriginalBlocksByDate(JSON.parse(JSON.stringify(sortedGroupedBlocks)));

    } catch (err) {
      console.error('❌ Ошибка загрузки программы:', err);
      setError('Не удалось загрузить программу');
    } finally {
      setLoading(false);
    }
  };

  // 🔥 ФУНКЦИИ ДЛЯ РАБОТЫ С ДОКЛАДЧИКАМИ (кнопками)
  const moveSpeakerUp = (blockId, speakerId) => {
    const newBlocksByDate = JSON.parse(JSON.stringify(blocksByDate));

    Object.values(newBlocksByDate).forEach(blocks => {
      const block = blocks.find(b => b.id === blockId);
      if (block) {
        if (block.type === 'plenary') {
          const speakers = [...plenarySpeakers];
          const index = speakers.findIndex(s => s.id === speakerId);
          if (index > 0) {
            [speakers[index - 1], speakers[index]] = [speakers[index], speakers[index - 1]];
            setPlenarySpeakers(speakers);
            setHasChanges(true);
            setHasSpeakerChanges(true);
          }
        } else if (block.type === 'section') {
          const speakers = [...(sectionSpeakers[block.sectionId] || [])];
          const index = speakers.findIndex(s => s.id === speakerId);
          if (index > 0) {
            [speakers[index - 1], speakers[index]] = [speakers[index], speakers[index - 1]];
            setSectionSpeakers(prev => ({ ...prev, [block.sectionId]: speakers }));
            setHasChanges(true);
            setHasSpeakerChanges(true);
          }
        }
      }
    });
  };

  const moveSpeakerDown = (blockId, speakerId) => {
    const newBlocksByDate = JSON.parse(JSON.stringify(blocksByDate));

    Object.values(newBlocksByDate).forEach(blocks => {
      const block = blocks.find(b => b.id === blockId);
      if (block) {
        if (block.type === 'plenary') {
          const speakers = [...plenarySpeakers];
          const index = speakers.findIndex(s => s.id === speakerId);
          if (index < speakers.length - 1) {
            [speakers[index], speakers[index + 1]] = [speakers[index + 1], speakers[index]];
            setPlenarySpeakers(speakers);
            setHasChanges(true);
            setHasSpeakerChanges(true);
          }
        } else if (block.type === 'section') {
          const speakers = [...(sectionSpeakers[block.sectionId] || [])];
          const index = speakers.findIndex(s => s.id === speakerId);
          if (index < speakers.length - 1) {
            [speakers[index], speakers[index + 1]] = [speakers[index + 1], speakers[index]];
            setSectionSpeakers(prev => ({ ...prev, [block.sectionId]: speakers }));
            setHasChanges(true);
            setHasSpeakerChanges(true);
          }
        }
      }
    });
  };

  // 🔥 ОБРАБОТЧИК DRAG-AND-DROP
  const handleDragStart = (event) => {
    const { active } = event;
    if (active.data.current?.type === 'block') {
      setActiveBlock(active.data.current.block);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    setActiveBlock(null);

    if (!over) return;
    if (over.id === active.id) return;
    if (active.data.current?.type !== 'block') return;

    // Находим исходную дату
    let sourceDate = null;
    let sourceBlock = null;
    let sourceIndex = -1;

    Object.entries(blocksByDate).forEach(([dateStr, blocks]) => {
      const index = blocks.findIndex(b => b.id === active.id);
      if (index !== -1) {
        sourceDate = dateStr;
        sourceBlock = blocks[index];
        sourceIndex = index;
      }
    });

    if (!sourceDate || !sourceBlock) return;

    // Создаём копию данных
    const newBlocksByDate = JSON.parse(JSON.stringify(blocksByDate));

    // Удаляем блок из исходной даты
    newBlocksByDate[sourceDate] = newBlocksByDate[sourceDate].filter(b => b.id !== active.id);

    // Определяем целевую дату
    let targetDate = null;
    let targetIndex = -1;

    if (over.data.current?.type === 'date-zone') {
      targetDate = over.data.current.date;
      targetIndex = newBlocksByDate[targetDate]?.length || 0;
    } else if (over.data.current?.type === 'block') {
      Object.entries(newBlocksByDate).forEach(([dateStr, blocks]) => {
        const index = blocks.findIndex(b => b.id === over.id);
        if (index !== -1) {
          targetDate = dateStr;
          targetIndex = index;
        }
      });
    }

    if (!targetDate) return;

    // Вставляем блок в целевую дату
    const blockToMove = { ...sourceBlock };

    if (blockToMove.startTime) {
      const oldDate = new Date(blockToMove.startTime);
      const time = format(oldDate, 'HH:mm');
      const newDate = makeLocalDateTime(targetDate, time);
      blockToMove.startTime = newDate.toISOString();
      blockToMove.time = time;
    }

    if (targetIndex === -1 || targetIndex >= newBlocksByDate[targetDate].length) {
      newBlocksByDate[targetDate].push(blockToMove);
    } else {
      newBlocksByDate[targetDate].splice(targetIndex, 0, blockToMove);
    }

    // Пересчитываем время для обеих дат
    if (newBlocksByDate[sourceDate]) {
      newBlocksByDate[sourceDate] = recalculateDayTimes(newBlocksByDate[sourceDate]);
    }
    newBlocksByDate[targetDate] = recalculateDayTimes(newBlocksByDate[targetDate]);

    setBlocksByDate(newBlocksByDate);
    setHasChanges(true);
  };

  // 🔥 ФУНКЦИИ ДЛЯ РЕДАКТИРОВАНИЯ ВРЕМЕНИ
  const handleTimeClick = (block) => {
    if (!canEditProgram) return;
    setTimeEditBlock(block);
    setTimeEditValue(block.time || '09:00');
  };

  const handleTimeSave = async () => {
    if (!timeEditBlock) return;

    setLoading(true);
    try {
      // Определяем смещение пользователя.
      const offsetStr = getTimezoneOffsetString();

      console.log('🕐 Отправляем время:', timeEditValue, 'смещение:', offsetStr);

      await scheduleAPI.updateScheduleItem(timeEditBlock.realId, {
        time: timeEditValue,
        timezone_offset: offsetStr  // например, "+05:00"
      });

      // Обновляем UI
      const newBlocksByDate = JSON.parse(JSON.stringify(blocksByDate));
      Object.values(newBlocksByDate).forEach(blocks => {
        const index = blocks.findIndex(b => b.id === timeEditBlock.id);
        if (index !== -1) {
          const currentStart = blocks[index].startTime
            ? new Date(blocks[index].startTime)
            : new Date();
          const dateStr = format(currentStart, 'yyyy-MM-dd');
          const newStartTime = makeLocalDateTime(dateStr, timeEditValue).toISOString();

          blocks[index] = {
            ...blocks[index],
            time: timeEditValue,
            startTime: newStartTime,
          };
        }
      });

      setBlocksByDate(newBlocksByDate);
      setTimeEditBlock(null);
      setHasChanges(true);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);

    } catch (err) {
      console.error('❌ Ошибка сохранения времени:', err);
      setError('Не удалось сохранить время');
    } finally {
      setLoading(false);
    }
};

  // 🔥 ИСПРАВЛЕННАЯ ФУНКЦИЯ СОХРАНЕНИЯ ВСЕХ ИЗМЕНЕНИЙ
const handleSaveChanges = async () => {
  setLoading(true);
  setError('');

  try {
    const blockUpdates = [];
    const speakerUpdates = [];

    // Сохраняем и порядок, и фактическую дату/время пункта.
    // Раньше drag-and-drop менял startTime только в React state, а на API
    // уходил только order. После перезагрузки backend возвращал старую дату.
    Object.entries(blocksByDate).forEach(([dateStr, blocks]) => {
      blocks.forEach((block, index) => {
        if (block.realId) {
          blockUpdates.push({
            id: block.realId,
            order: index,
            start_time: block.startTime
          });
        }
      });
    });

    // Сохраняем порядок докладчиков
    plenarySpeakers.forEach((speaker, index) => {
      speakerUpdates.push({
        id: speaker.id,
        order: index
      });
    });

    Object.entries(sectionSpeakers).forEach(([sectionId, speakers]) => {
      speakers.forEach((speaker, index) => {
        speakerUpdates.push({
          id: speaker.id,
          order: index
        });
      });
    });

    // Отправляем обновления блоков: порядок + start_time.
    if (blockUpdates.length > 0) {
      for (const update of blockUpdates) {
        await scheduleAPI.updateScheduleItem(update.id, {
          order: update.order,
          start_time: update.start_time
        });
      }
    }

    if (speakerUpdates.length > 0) {
      await participantsAPI.bulkUpdateSpeakerOrder(speakerUpdates);
    }

    setOriginalBlocksByDate(JSON.parse(JSON.stringify(blocksByDate)));
    setHasChanges(false);
    setHasSpeakerChanges(false);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);

  } catch (err) {
    console.error('❌ Ошибка сохранения:', err);
    setError('Не удалось сохранить изменения');
  } finally {
    setLoading(false);
  }
};

  const handleAddClick = () => {
    setEditingBlock(null);
    setFormData({ title: '' });
    setShowAddForm(true);
  };

  const handleEditBlock = (block) => {
    setEditingBlock(block);
    setFormData({ title: block.title });
    setShowAddForm(true);
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('Удалить этот пункт из программы?')) return;

    setLoading(true);
    setError('');

    try {
      let blockToDelete = null;
      Object.values(blocksByDate).forEach(blocks => {
        const found = blocks.find(b => b.id === blockId);
        if (found) blockToDelete = found;
      });

      if (blockToDelete && blockToDelete.realId) {
        await scheduleAPI.deleteScheduleItem(blockToDelete.realId);
        await loadProgramData();
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      console.error('❌ Ошибка удаления:', err);
      setError(err.response?.data?.error || 'Не удалось удалить пункт');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async () => {
    setLoading(true);
    try {
      const dates = getDatesBetween(eventStartDate, eventEndDate);
      const firstDay = dates[0];
      firstDay.setHours(12, 0, 0, 0);

      const dataToSend = {
        event: eventId,
        section: null,
        is_plenary: false,
        participant: null,
        title: formData.title,
        description: '',
        start_time: firstDay.toISOString(),
        order: Object.values(blocksByDate).flat().length
      };

      if (editingBlock) {
        await scheduleAPI.updateScheduleItem(editingBlock.realId, dataToSend);
      } else {
        await scheduleAPI.createScheduleItem(dataToSend);
      }

      await loadProgramData();
      setShowAddForm(false);
      setEditingBlock(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('❌ Ошибка сохранения:', err);
      setError(err.response?.data?.error || 'Не удалось сохранить блок');
    } finally {
      setLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowAddForm(false);
    setEditingBlock(null);
    setFormData({ title: '' });
  };

  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return '?';
    return `${(firstName?.[0] || '')}${(lastName?.[0] || '')}`.toUpperCase();
  };

  const getBlockIcon = (title) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('кофе') || lowerTitle.includes('coffee')) return <CoffeeIcon />;
    if (lowerTitle.includes('обед') || lowerTitle.includes('lunch')) return <LunchDiningIcon />;
    if (lowerTitle.includes('регистрац')) return <EmojiPeopleIcon />;
    return <AccessTimeIcon />;
  };

  const isInitialLoading = loading && !Object.keys(blocksByDate).length;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
      <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <StyledDialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ScheduleIcon sx={{ fontSize: 28 }} />
            <Typography variant="h5" fontWeight="700">
              {isSectionPage ? 'Программа секции' : 'Программа мероприятия'}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              width: 38,
              height: 38,
              color: 'text.secondary',
              backgroundColor: alpha(theme.palette.common.white, 0.42),
              border: `1px solid ${alpha(theme.palette.common.white, 0.78)}`,
              boxShadow: `0 6px 18px ${alpha(theme.palette.common.black, 0.05)}`,
              '&:hover': {
                color: 'text.primary',
                backgroundColor: alpha(theme.palette.common.white, 0.70),
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </StyledDialogTitle>

        <StyledDialogContent>
          {isInitialLoading ? (
            <ProgramSkeleton theme={theme} />
          ) : (
            <>
          {success && (
            <Alert severity="success" sx={{
              mb: 2,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
              background: `linear-gradient(145deg, ${alpha(theme.palette.success.light, 0.26)}, ${alpha(theme.palette.common.white, 0.46)})`,
            }}>
              Изменения сохранены
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{
              mb: 2,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
              background: `linear-gradient(145deg, ${alpha(theme.palette.error.light, 0.22)}, ${alpha(theme.palette.common.white, 0.46)})`,
            }}>
              {error}
            </Alert>
          )}

          {/* Заголовок мероприятия */}
          <Box sx={{ mt: 1.2, mb: 2.4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.35rem', sm: '1.65rem' },
                lineHeight: 1.2,
                color: 'text.primary',
              }}
            >
              {eventTitle || 'Мероприятие'}
              {isSectionPage && currentSectionId && (
                <Typography component="span" variant="h6" sx={{ ml: 1.2, color: 'text.secondary', fontSize: '0.95em', fontWeight: 500 }}>
                  • {sections.find(s => s.id === currentSectionId)?.title}
                </Typography>
              )}
            </Typography>
          </Box>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {Object.entries(blocksByDate).map(([dateStr, dayBlocks]) => (
              <DateDropZone key={dateStr} date={dateStr}>
                {/* Заголовок даты */}
                <Typography
                  variant="h6"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: '0.96rem',
                    mb: 1.2,
                    pb: 0.8,
                    borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
                  }}
                >
                  {formatDateHeader(parseISO(dateStr))}
                </Typography>

                {/* Контейнер для блоков этого дня */}
                <SortableContext items={dayBlocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                  {dayBlocks.map((block) => {
                    // 🔥 ОПРЕДЕЛЯЕМ ПРАВА ДЛЯ ЭТОГО БЛОКА
                    let canDragBlock = false;
                    let canDragSpeakers = false;

                    if (canManageEverything) {
                      canDragBlock = true;
                      canDragSpeakers = true;
                    } else if (block.type === 'plenary' && isPlenaryModerator) {
                      canDragSpeakers = true;
                    } else if (block.type === 'section' && block.sectionId) {
                      const isThisSectionModerator = isSectionModerator(block.sectionId);
                      if (isThisSectionModerator) {
                        canDragSpeakers = true;
                      }
                    }

                    return (
                      <SortableBlock
                        key={block.id}
                        block={block}
                        isDragDisabled={!canDragBlock}
                      >
                        <BlockCard isplenary={block.type === 'plenary'}>
                          <BlockHeader>
                            <DragIndicatorIcon sx={{ color: 'text.disabled', fontSize: 19, opacity: 0.72 }} />

                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                              {block.type === 'plenary' && <StarIcon sx={{ color: theme.palette.warning.main }} />}
                              {block.type === 'section' && <MenuBookIcon sx={{ color: block.color || theme.palette.primary.main }} />}
                              {block.type === 'custom' && getBlockIcon(block.title)}

                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 650, fontSize: '0.92rem', lineHeight: 1.3 }}
                              >
                                {block.title}
                              </Typography>

                              {block.type === 'plenary' && (
                                <PlenaryBadge label="Пленарное" size="small" icon={<StarIcon />} />
                              )}
                              {block.type === 'section' && (
                                <SectionBadge label="Секция" size="small" color={block.color} />
                              )}
                            </Box>

                            <TimeChip
                              icon={<AccessTimeIcon />}
                              label={block.time}
                              color={block.type === 'plenary' ? theme.palette.warning.main : block.color || theme.palette.grey[600]}
                              onClick={() => handleTimeClick(block)}
                            />

                            {block.type === 'custom' && canManageEverything && (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteBlock(block.id)}
                                sx={{ color: theme.palette.error.main }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </BlockHeader>

                          {/* Докладчики пленарного заседания - только на главной странице */}
{block.type === 'plenary' && !isSectionPage && plenarySpeakers.length > 0 && (
  <Box sx={{ mt: 2 }}>
    {plenarySpeakers.map((speaker, idx) => (
      <SpeakerRow key={speaker.id}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 24 }}>
          {idx + 1}.
        </Typography>
        <Avatar
          src={speaker.user?.avatar}
          sx={{
            width: 30,
            height: 30,
            border: `1px solid ${alpha(theme.palette.common.white, 0.76)}`,
            boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          {getInitials(speaker.user?.first_name, speaker.user?.last_name)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight="500">
            {speaker.user?.first_name} {speaker.user?.last_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {speaker.talk_title}
          </Typography>
        </Box>

        {/* Кнопки для изменения порядка */}
        {(canManageEverything || (block.type === 'plenary' && isPlenaryModerator)) && (
          <Box>
            <IconButton
              size="small"
              onClick={() => moveSpeakerUp(block.id, speaker.id)}
              disabled={idx === 0}
            >
              <ArrowUpIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => moveSpeakerDown(block.id, speaker.id)}
              disabled={idx === plenarySpeakers.length - 1}
            >
              <ArrowDownIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </SpeakerRow>
    ))}
  </Box>
)}

{/* Докладчики секции - только на странице секции и только для текущей секции */}
{block.type === 'section' && isSectionPage && block.sectionId === currentSectionId &&
 sectionSpeakers[block.sectionId]?.length > 0 && (
  <Box sx={{ mt: 2 }}>
    {sectionSpeakers[block.sectionId].map((speaker, idx) => (
      <SpeakerRow key={speaker.id}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 24 }}>
          {idx + 1}.
        </Typography>
        <Avatar
          src={speaker.user?.avatar}
          sx={{
            width: 30,
            height: 30,
            border: `1px solid ${alpha(theme.palette.common.white, 0.76)}`,
            boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          {getInitials(speaker.user?.first_name, speaker.user?.last_name)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight="500">
            {speaker.user?.first_name} {speaker.user?.last_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {speaker.talk_title}
          </Typography>
        </Box>

        {/* Кнопки для изменения порядка */}
        {(canManageEverything ||
          (block.type === 'section' && isSectionModerator(block.sectionId))) && (
          <Box>
            <IconButton
              size="small"
              onClick={() => moveSpeakerUp(block.id, speaker.id)}
              disabled={idx === 0}
            >
              <ArrowUpIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => moveSpeakerDown(block.id, speaker.id)}
              disabled={idx === sectionSpeakers[block.sectionId].length - 1}
            >
              <ArrowDownIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </SpeakerRow>
    ))}
  </Box>
)}
                        </BlockCard>
                      </SortableBlock>
                    );
                  })}
                </SortableContext>
              </DateDropZone>
            ))}

            <DragOverlay>
              {activeBlock && (
                <BlockCard isplenary={activeBlock.type === 'plenary'}>
                  <BlockHeader>
                    <DragIndicatorIcon sx={{ color: 'text.disabled', fontSize: 19, opacity: 0.72 }} />
                    <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 650, fontSize: '0.92rem', lineHeight: 1.3 }}
                              >
                      {activeBlock.title}
                    </Typography>
                  </BlockHeader>
                </BlockCard>
              )}
            </DragOverlay>
          </DndContext>

          {canManageEverything && !showAddForm && (
            <AddButton fullWidth startIcon={<AddIcon />} onClick={handleAddClick}>
              Добавить пункт в расписание
            </AddButton>
          )}

          {canManageEverything && showAddForm && (
            <FormCard elevation={0}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 2 }}>
                {editingBlock ? 'Редактирование блока' : 'Новый блок'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Название блока *"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Например: Кофе-брейк, Регистрация, Обед..."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                  <Button variant="outlined" onClick={handleFormCancel} startIcon={<CancelIcon />} sx={{
                  borderRadius: 999,
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 650,
                }}>
                    Отмена
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleFormSubmit}
                    disabled={loading || !formData.title.trim()}
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    sx={{
                  borderRadius: 999,
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 650,
                }}
                  >
                    {loading ? 'Сохранение...' : editingBlock ? 'Сохранить' : 'Добавить'}
                  </Button>
                </Box>
              </Box>
            </FormCard>
          )}

          {/* 🔥 КНОПКА СОХРАНЕНИЯ - ТЕПЕРЬ ПОКАЗЫВАЕТСЯ И ДЛЯ МОДЕРАТОРОВ */}
          {(hasChanges && (canManageEverything || hasSpeakerChanges)) && (
            <Box sx={{
              position: 'sticky',
              bottom: 0,
              mt: 2,
              p: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.78)}, ${alpha(theme.palette.common.white, 0.42)})`,
              border: `1px solid ${alpha(theme.palette.common.white, 0.70)}`,
              borderRadius: 3,
              boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.08)}`,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              zIndex: 10
            }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setBlocksByDate(JSON.parse(JSON.stringify(originalBlocksByDate)));
                  setHasChanges(false);
                  setHasSpeakerChanges(false);
                }}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 650,
                }}
              >
                Отмена
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleSaveChanges}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 650,
                }}
              >
                {loading ? 'Сохранение...' : 'Сохранить изменения'}
              </Button>
            </Box>
          )}

          <MuiDialog open={!!timeEditBlock} onClose={() => setTimeEditBlock(null)}>
            <MuiDialogTitle>Редактирование времени</MuiDialogTitle>
            <MuiDialogContent>
              <TextField
                type="time"
                value={timeEditValue}
                onChange={(e) => setTimeEditValue(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 2 }}
              />
            </MuiDialogContent>
            <DialogActions>
              <Button onClick={() => setTimeEditBlock(null)}>Отмена</Button>
              <Button onClick={handleTimeSave} variant="contained" color="success">
                Сохранить
              </Button>
            </DialogActions>
          </MuiDialog>
            </>
          )}
        </StyledDialogContent>
      </StyledDialog>
    </LocalizationProvider>
  );
};

export default EventProgramModal;